// Apply to the agency (public) — un créateur TikTok loggé non rattaché dépose
// sa candidature. Adapté de Black OS (références "Black Ads" retirées), insère
// dans black_agency_applications. Pré-rempli depuis l'identité TikTok. i18n.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../utils/creatorLang';
import {
  CreatorScreen, ScreenHeader, Card, SectionLabel, BottomNav, I,
} from '../../components/creator/primitives';
import { PublicSettingsButton } from '../../components/creator/PublicSettingsButton';

const SERVERS = ['MENA', 'FR+', 'IT+', 'DE+', 'US+', 'Other'];
const inputStyle = {
  width: '100%', fontSize: 16, padding: '12px 14px', borderRadius: 10,
  background: 'var(--bg-3)', border: '1px solid var(--line-1)', color: 'var(--fg-0)',
};

const Apply = () => {
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null);
  const [serverInterest, setServerInterest] = useState('MENA');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user || user.user_metadata?.role !== 'creator') { navigate('/my/login', { replace: true }); return; }
      let followers = null;
      try { followers = JSON.parse(localStorage.getItem('creator_tiktok') || 'null')?.stats?.follower_count ?? null; } catch { /* ignore */ }
      setIdentity({
        tiktok_open_id: user.user_metadata?.tiktok_open_id ?? null,
        tiktok_username: user.user_metadata?.tiktok_display_name ?? null,
        display_name: user.user_metadata?.tiktok_display_name ?? null,
        follower_count: followers,
      });
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const { error } = await supabase.from('black_agency_applications').insert({
        tiktok_open_id: identity?.tiktok_open_id ?? null,
        tiktok_username: identity?.tiktok_username ?? null,
        display_name: identity?.display_name ?? null,
        follower_count: identity?.follower_count ?? null,
        server_interest: serverInterest,
        message: message.trim() || null,
        contact: contact.trim() || null,
      });
      if (error) throw error;
      setDone(true);
    } catch (e) {
      setErr(e?.message || t('pub_apply_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CreatorScreen>
      <ScreenHeader title={t('pub_join_title')} subtitle={t('pub_join_desc')} onBack={() => navigate('/my/public-dashboard')} right={<PublicSettingsButton />} />

      <div style={{ padding: '16px 20px 96px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {done ? (
          <Card pad={20}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <I.Check size={22} stroke="#4ade80" />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg-0)', marginBottom: 6 }}>{t('pub_apply_received')}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{t('pub_apply_thanks')}</div>
            </div>
          </Card>
        ) : (
          <>
            <Card pad={16}>
              <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.55 }}>
                {t('pub_apply_intro', { name: identity?.display_name || '—' })}
              </div>
            </Card>

            <div>
              <SectionLabel>{t('pub_apply_server')}</SectionLabel>
              <select value={serverInterest} onChange={(e) => setServerInterest(e.target.value)} style={inputStyle}>
                {SERVERS.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
              </select>
            </div>

            <div>
              <SectionLabel>{t('pub_apply_contact')}</SectionLabel>
              <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t('pub_apply_contact_ph')} style={inputStyle} />
            </div>

            <div>
              <SectionLabel>{t('pub_apply_message')}</SectionLabel>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder={t('pub_apply_message_ph')} style={{ ...inputStyle, resize: 'vertical', minHeight: 96 }} />
            </div>

            {err && <div style={{ fontSize: 12, color: '#f87171', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, padding: '10px 12px' }}>{err}</div>}

            <button onClick={submit} disabled={busy} style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--accent-pink)', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700, opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <I.Sparkle size={15} stroke="currentColor" /> {busy ? t('pub_apply_submitting') : t('pub_apply_submit')}
            </button>
          </>
        )}
      </div>

      <BottomNav active="apply" variant="public" />
    </CreatorScreen>
  );
};

export default Apply;
